import React from 'react';
import { Row } from '@tanstack/react-table';
import { useVirtualizer } from '@tanstack/react-virtual';

import { Table } from './Table';
import { useTableContext } from './TableContext';

type RenderRowParams<TData> = {
  row: Row<TData>;
  rowKey?: React.Key;
  rowIndex: number;
  measureRowElement?: (element: HTMLTableRowElement | null) => void;
  virtualRowStart?: number;
};

export type TableVirtualBodyProps<TData> = {
  rows: Row<TData>[];
  columnCount: number;
  dataRole?: string;
  estimateRowHeight?: number;
  overscan?: number;
  renderRow: (params: RenderRowParams<TData>) => React.ReactNode;
};

const DEFAULT_ESTIMATE_ROW_HEIGHT = 42;
const DEFAULT_OVERSCAN = 16;

export function TableVirtualBody<TData>({
  rows,
  renderRow,
  columnCount,
  overscan = DEFAULT_OVERSCAN,
  dataRole = 'table-scrollable',
  estimateRowHeight = DEFAULT_ESTIMATE_ROW_HEIGHT,
}: TableVirtualBodyProps<TData>) {
  const { shouldVirtualize } = useTableContext();
  const bodyScrollRef = React.useRef<HTMLTableSectionElement | null>(null);

  const rowVirtualizer = useVirtualizer<HTMLTableSectionElement, HTMLTableRowElement>({
    overscan,
    count: rows.length,
    enabled: shouldVirtualize,
    getScrollElement: React.useCallback(() => bodyScrollRef.current, []),
    estimateSize: React.useCallback(() => estimateRowHeight, [estimateRowHeight]),
    measureElement: React.useCallback((element) => element.getBoundingClientRect().height, []),
  });
  const totalSize = rowVirtualizer.getTotalSize();

  const virtualRows = shouldVirtualize ? rowVirtualizer.getVirtualItems() : null;

  const spacerStyle = React.useMemo(() => ({ height: totalSize }), [totalSize]);

  const bodyRows =
    shouldVirtualize && virtualRows ? (
      <>
        <Table.Row key="virtualized-body-spacer" spacer aria-hidden>
          <Table.Cell spacer colSpan={columnCount} style={spacerStyle} />
        </Table.Row>
        {virtualRows.map((virtualRow, virtualRowSlotIndex) => {
          const row = rows[virtualRow.index];

          if (!row) {
            return null;
          }

          return renderRow({
            row,
            rowKey: `virtual-row-${virtualRowSlotIndex}`,
            measureRowElement: rowVirtualizer.measureElement,
            rowIndex: virtualRow.index,
            virtualRowStart: virtualRow.start,
          });
        })}
      </>
    ) : (
      rows.map((row, rowIndex) => renderRow({ row, rowIndex }))
    );

  return (
    <Table.Body ref={bodyScrollRef} data-role={dataRole}>
      {bodyRows}
    </Table.Body>
  );
}
